/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
/* eslint-disable arrow-body-style */
import React, {useState} from 'react';
import {ListGroup, InputGroup, FormControl, Form} from 'react-bootstrap';

import type {IConditionForm, ConditionObject} from 'interfaces';
import {
	resolveFieldKey,
	getNestedField,
} from 'pages/Policy/components/forms/utils/InitConditionLabels';

import type {TransformedItem} from 'pages/Policy/components/forms/utils/InitConditionLabels';

interface ListGroupConditionProps {
	options: TransformedItem[];
	formData: IConditionForm;
	onSelect: (key: string, ...path: string[]) => void;
	nameDropdown: string;
}

const CustomListGroupCondition = ({
	options,
	formData,
	onSelect,
	nameDropdown,
}: ListGroupConditionProps) => {
	const [filter, setFilter] = useState('');

	/**
	 * Filters a tree of TransformedItem nodes based on a given text input.
	 *
	 * This function is designed to work with a hierarchical list of conditions (e.g., UI filter categories).
	 * It returns a filtered version of the tree, preserving the full path of parents for any matching item,
	 * and ensuring children are displayed when their parent matches the filter.
	 *
	 * Specifically:
	 * - If a node's category matches the filter string (case-insensitive), that node and all its children
	 *   are included in the result (even if they don’t match individually).
	 * - If a child node matches but the parent doesn't, the parent is still included to preserve the hierarchy.
	 * - Non-matching branches are removed entirely.
	 * - The `visibleSwitch` and other properties of each node are preserved.
	 *
	 * This behavior ensures that:
	 * - Searching for a parent like "TAI" will show its full subtree, e.g., "TAI > TAC > PLMN_ID > MNC".
	 * - Searching for a child like "TAC" will still include all necessary parents, e.g., "Location > TAI > TAC".
	 * - The resulting tree is structurally correct and visually useful in hierarchical UIs.
	 *
	 * @param items The full list of root-level TransformedItem nodes to be filtered.
	 * @param filterSelected The user-entered text string used to match categories in the tree.
	 * @returns A filtered list of TransformedItem nodes that match the search term directly or through descendants.
	 */
	const filterTransformedItems = (
		items: TransformedItem[],
		filterSelected: string,
	): TransformedItem[] => {
		const lowerFilter = filterSelected.toLowerCase();

		const filterRecursive = (item: TransformedItem): TransformedItem | null => {
			const selfMatch = item.category.toLowerCase().includes(lowerFilter);

			const filteredChildren = (item.items || [])
				.map(filterRecursive)
				.filter((x): x is TransformedItem => x !== null);

			if (selfMatch || filteredChildren.length > 0) {
				return {
					...item,
					items: selfMatch ? item.items : filteredChildren,
				};
			}

			return null;
		};

		return items
			.map(filterRecursive)
			.filter((item): item is TransformedItem => item !== null);
	};

	const filteredOptions = filter
		? filterTransformedItems(options, filter)
		: options;

	const isConditionObject = (
		maybeField: any,
	): maybeField is ConditionObject<any> => {
		return (
			typeof maybeField === 'object' &&
			maybeField !== null &&
			'value' in maybeField &&
			'active' in maybeField
		);
	};

	const computeIsActive = (path: string[], category?: string): boolean => {
		if (category === 'MNC / MCC') {
			if (!Array.isArray(formData.servingNetwork)) return false;
			return formData.servingNetwork.some(sn => sn.mcc.active || sn.mnc.active);
		}

		const fieldKey = resolveFieldKey(...path);
		if (!fieldKey) return false;

		let field: any;

		if (Array.isArray(fieldKey)) {
			// Verificamos si el primer nivel es un array complejo (como tai: ITaiForm[])
			const firstLevel = formData[fieldKey[0] as keyof IConditionForm];
			if (Array.isArray(firstLevel)) {
				// Es un array complejo → evaluamos cada elemento con getNestedField
				return firstLevel.some(item => {
					const nested = getNestedField(item, fieldKey.slice(1));
					if (isConditionObject(nested)) return nested.active;
					if (typeof nested === 'object' && nested !== null) {
						return Object.values(nested).some(isConditionObjectAndActive);
					}
					return false;
				});
			}

			// Si no es array complejo, usamos getNestedField normal
			field = getNestedField(formData, fieldKey);
		} else {
			field = formData[fieldKey as keyof IConditionForm];
		}

		if (isConditionObject(field)) {
			return field.active;
		}

		if (typeof field === 'object' && field !== null) {
			return Object.values(field).some(isConditionObjectAndActive);
		}

		return false;
	};

	const isConditionObjectAndActive = (val: any): val is ConditionObject<any> =>
		isConditionObject(val) && val.active;

	const renderItems = (
		items: TransformedItem[],
		parentPath: string[] = [],
	): React.ReactNode[] => {
		return items.map(item => {
			const isLeaf = !item.items || item.items.length === 0;
			const newPath = [...parentPath, item.category];

			if (isLeaf) {
				const isActive =
					item.category === 'MNC / MCC'
						? computeIsActive(parentPath, item.category)
						: computeIsActive(newPath, item.category);

				return (
					<ListGroup.Item
						key={newPath.join('-')}
						action
						className={`subitem d-flex justify-content-between align-items-center ${
							isActive ? 'active-item' : ''
						}`}
						onClick={e => {
							e.preventDefault();
							e.stopPropagation();
							onSelect(formData.id, ...newPath);
						}}>
						<div>
							<i className="bi bi-dot" />
							{item.category}
						</div>
					</ListGroup.Item>
				);
			} else {
				return (
					<React.Fragment key={newPath.join('-')}>
						<div className={`category-context level-${parentPath.length}`}>
							<ListGroup.Item className="category-header d-flex justify-content-between align-items-center category-header-fake-disabled">
								<div>
									<i className="bi bi-dash" />
									{item.category}
								</div>
							</ListGroup.Item>
							{renderItems(item.items, newPath)}
						</div>
					</React.Fragment>
				);
			}
		});
	};

	return (
		<div className="rounded-3 &gt; border">
			<div className="centered-div mb-2">
				<label htmlFor="filter-list-group" className="centered-label">
					{nameDropdown}
				</label>
			</div>
			<div className="mb-2 mr-3 ml-3 blur-div">
				<InputGroup className="w-auto input-with-icon">
					<FormControl
						id="filter-list-group"
						placeholder="Type to filter..."
						value={filter}
						onChange={(e: any) => setFilter(e.target.value)}
					/>
					<i className="bi bi-search" title="Search" data-toggle="tooltip" />
				</InputGroup>
			</div>
			<div className="scrollable-list &gt; bg-light my-3 mx-3 pr-3">
				<ListGroup>{renderItems(filteredOptions)}</ListGroup>
			</div>
		</div>
	);
};

export default CustomListGroupCondition;
